/** Shared pantry constants and helpers for YourPantry v2. */

export const PERISHABLE_DAYS = 10
export const EXPIRES_SOON_HOURS = 48
export const EXPIRING_BANNER_DAYS = 5
export const LOW_STOCK_THRESHOLD = 15
export const PANTRY_STARTER_SESSION_KEY = 'agent_pantry_starter_session_dismissed_v1'
export const PANTRY_STARTER_TAB_SESSION_KEY = 'pantry_starter_active_tab'

/** Coming back to pantry setup opens Staples, where edits usually happen. */
export function resetPantryStarterToStaples() {
  try {
    sessionStorage.setItem(PANTRY_STARTER_TAB_SESSION_KEY, 'staples')
  } catch {
    /* ignore */
  }
}

export const PANTRY_CATEGORIES = [
  { key: 'fruits_vegetables', label: 'Produce', icon: '🥦' },
  { key: 'proteins', label: 'Proteins', icon: '🥩' },
  { key: 'dairy', label: 'Dairy', icon: '🧀' },
  { key: 'spices', label: 'Spices', icon: '🧂' },
  { key: 'staples', label: 'Staples', icon: '🫙' },
]

/** API category values that group into each pantry bucket. */
export const BUCKET_TO_CATEGORY = {
  fruits_vegetables: 'PRODUCE',
  proteins: 'MEAT',
  dairy: 'DAIRY',
  spices: 'SPICES',
  staples: 'STAPLES',
}

/** @deprecated Colors live in design-tokens.css (`--pantry-tile-*`). Use IngredientTile category classes. */
export const CATEGORY_TILE_COLORS = {
  fruits_vegetables: { bg: 'var(--pantry-tile-produce-bg)', fg: 'var(--pantry-tile-produce-fg)' },
  proteins: { bg: 'var(--pantry-tile-protein-bg)', fg: 'var(--pantry-tile-protein-fg)' },
  dairy: { bg: 'var(--pantry-tile-dairy-bg)', fg: 'var(--pantry-tile-dairy-fg)' },
  spices: { bg: 'var(--pantry-tile-spices-bg)', fg: 'var(--pantry-tile-spices-fg)' },
  staples: { bg: 'var(--pantry-tile-staples-bg)', fg: 'var(--pantry-tile-staples-fg)' },
  default: { bg: 'var(--pantry-tile-default-bg)', fg: 'var(--pantry-tile-default-fg)' },
}

/** Hours until backend perishable expiry (false when not applicable). */
export function getHoursUntilExpiration(item) {
  if (item?.frozen) return false
  if (!item?.perishable || !item?.date_updated) return false
  const updated = new Date(item.date_updated)
  if (Number.isNaN(updated.getTime())) return false
  const expiresAt = new Date(updated)
  expiresAt.setDate(expiresAt.getDate() + PERISHABLE_DAYS)
  return (expiresAt - Date.now()) / (1000 * 60 * 60)
}

/** True when the item is marked frozen (freezer storage; pauses fridge expiry when perishable). */
export function isFrozen(item) {
  return Boolean(item?.frozen)
}

/** Buckets that may carry a frozen/freezer label. */
export const FREEZABLE_BUCKET_KEYS = ['proteins', 'dairy', 'staples']

export function isFreezableBucketKey(bucketKey) {
  return FREEZABLE_BUCKET_KEYS.includes(bucketKey)
}

/** Whole days left until expiry; null when not perishable or already expired. */
export function getDaysLeft(item) {
  const hoursLeft = getHoursUntilExpiration(item)
  if (!Number.isFinite(hoursLeft) || hoursLeft <= 0) return null
  return Math.ceil(hoursLeft / 24)
}

/**
 * Quiet expiry cue for pantry rows.
 * Window is days left within EXPIRING_BANNER_DAYS (not calendar week).
 * @returns {{ tone: 'soon' | 'later', label: string } | null}
 */
export function getExpiryBadgeMeta(daysLeft) {
  if (daysLeft == null || daysLeft < 1 || daysLeft > EXPIRING_BANNER_DAYS) return null
  if (daysLeft <= 2) return { tone: 'soon', label: 'Use soon' }
  // 3–5 days: soft cue only — avoid "This week" calendar urgency.
  return { tone: 'later', label: 'A few days' }
}

export function isExpiringWithinDays(item, days) {
  const hoursLeft = getHoursUntilExpiration(item)
  return Number.isFinite(hoursLeft) && hoursLeft > 0 && hoursLeft <= days * 24
}

export function isExpiresSoon(item) {
  const hoursLeft = getHoursUntilExpiration(item)
  return Number.isFinite(hoursLeft) && hoursLeft <= EXPIRES_SOON_HOURS && hoursLeft > 0
}

export function pantryItemDisplayName(item) {
  return (item?.display_name || item?.item_name || '').trim()
}

export function categoryToBucketKey(category) {
  if (!category) return 'staples'
  const c = String(category).toUpperCase()
  if (['MEAT', 'DELI', 'SEAFOOD'].some((x) => c.includes(x))) return 'proteins'
  if (c.includes('PRODUCE')) return 'fruits_vegetables'
  if (c.includes('DAIRY')) return 'dairy'
  if (c.includes('SPICES')) return 'spices'
  return 'staples'
}

const UNIT_PLURALS = {
  each: 'each',
  can: 'cans',
  bag: 'bags',
  bottle: 'bottles',
  jar: 'jars',
  box: 'boxes',
  pack: 'packs',
  bunch: 'bunches',
  head: 'heads',
  cup: 'cups',
  lb: 'lbs',
  oz: 'oz',
  container: 'containers',
  loaf: 'loaves',
  tube: 'tubes',
}

export function formatQuantityUnit(unit, qty = 1) {
  const u = (unit || '').trim().toLowerCase()
  if (!u || u === 'each') return null
  if (qty === 1) return u
  return UNIT_PLURALS[u] || `${u}s`
}

/** Display label for an item count, e.g. "2 cans" or "2". */
export function formatPantryQuantity(item) {
  const qty = item?.quantity
  if (qty == null || qty <= 0) return null
  const unitLabel = formatQuantityUnit(item?.quantity_unit, qty)
  if (unitLabel) return `${qty} ${unitLabel}`
  return String(qty)
}

/** Produce forms that stay as a label (a head of garlic), not an amount you adjust. */
const NON_EDITABLE_QUANTITY_UNITS = new Set([
  'head',
  'heads',
  'bunch',
  'bunches',
  'bulb',
  'bulbs',
])

/** True when quantity can be adjusted via tap-to-edit (discrete counts, not fixed forms). */
export function isPantryQuantityEditable(item) {
  const unit = (item?.quantity_unit || '').trim().toLowerCase()
  if (!unit || unit === 'each') return true
  return !NON_EDITABLE_QUANTITY_UNITS.has(unit)
}

const WORD_COUNTS = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
  twenty: 20,
}

const WORD_COUNT_PATTERN =
  /^(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty)\s+(.+)$/i

/** Reject bare leading counts when the remainder starts with a measure unit ("2 oz butter"). */
const LEADING_MEASURE_UNIT_PATTERN =
  /^(oz|ounce|ounces|lb|lbs|pound|pounds|kg|g|ml|l|cup|cups|tbsp|tsp|fl\.?\s*oz)\b/i

/** Package-size measures — not inventory counts (e.g. "Cheese - 8 Oz"). */
const PACKAGE_MEASURE_UNIT_PATTERN =
  /^(oz|ounce|ounces|lb|lbs|pound|pounds|kg|g|ml|l|fl\.?\s*oz|ct|pk|pack)\b/i

const PACKAGE_SIZE_UNIT_ALT =
  'oz|ounce|ounces|lb|lbs|pound|pounds|kg|g|ml|l|fl\\.?\\s*oz|ct|pk|pack'

/**
 * Countable container units from initial add text (e.g. "Pineapple juice - 6 cans").
 * Keys are accepted spellings; values are stored singular forms.
 */
const COUNT_UNIT_ALIASES = {
  can: 'can',
  cans: 'can',
  bag: 'bag',
  bags: 'bag',
  bottle: 'bottle',
  bottles: 'bottle',
  jar: 'jar',
  jars: 'jar',
  box: 'box',
  boxes: 'box',
  pack: 'pack',
  packs: 'pack',
  packet: 'pack',
  packets: 'pack',
  bunch: 'bunch',
  bunches: 'bunch',
  head: 'head',
  heads: 'head',
  container: 'container',
  containers: 'container',
  loaf: 'loaf',
  loaves: 'loaf',
  tube: 'tube',
  tubes: 'tube',
  each: 'each',
  piece: 'each',
  pieces: 'each',
}

const COUNT_UNIT_PATTERN = Object.keys(COUNT_UNIT_ALIASES).sort((a, b) => b.length - a.length).join('|')

/** Weight and volume amounts a cook would type ("2 lbs", "8 oz", "2 cups"). */
const MEASURE_UNIT_ALIASES = {
  lb: 'lb',
  lbs: 'lb',
  pound: 'lb',
  pounds: 'lb',
  oz: 'oz',
  ounce: 'oz',
  ounces: 'oz',
  cup: 'cup',
  cups: 'cup',
}

const MEASURE_UNIT_PATTERN = Object.keys(MEASURE_UNIT_ALIASES).sort((a, b) => b.length - a.length).join('|')

/** Units offered when adding or editing a pantry amount. Blank is a plain count. */
export const PANTRY_QUANTITY_UNIT_OPTIONS = [
  { value: '', label: 'count' },
  { value: 'lb', label: 'lb' },
  { value: 'oz', label: 'oz' },
  { value: 'cup', label: 'cup' },
  { value: 'can', label: 'can' },
  { value: 'bag', label: 'bag' },
  { value: 'bottle', label: 'bottle' },
  { value: 'jar', label: 'jar' },
  { value: 'box', label: 'box' },
  { value: 'pack', label: 'pack' },
  { value: 'bunch', label: 'bunch' },
  { value: 'head', label: 'head' },
]

function normalizeCountUnit(rawUnit) {
  const key = String(rawUnit || '')
    .trim()
    .toLowerCase()
    .replace(/\.$/, '')
  return COUNT_UNIT_ALIASES[key] || null
}

function normalizeMeasureUnit(rawUnit) {
  const key = String(rawUnit || '')
    .trim()
    .toLowerCase()
    .replace(/\.$/, '')
  return MEASURE_UNIT_ALIASES[key] || null
}

/** Stored unit for an amount field. "each" and blanks stay empty so the row shows a plain count. */
export function canonicalPantryQuantityUnit(rawUnit) {
  const cleaned = String(rawUnit || '')
    .trim()
    .toLowerCase()
    .replace(/\.$/, '')
  if (!cleaned || cleaned === 'each') return ''
  return normalizeCountUnit(cleaned) || normalizeMeasureUnit(cleaned) || cleaned
}

function pantryAddQuantityResult(name, quantity, quantityUnit = null) {
  const cleaned = String(name || '').trim()
  if (!cleaned || !/[A-Za-z]/.test(cleaned)) return null
  if (!Number.isFinite(quantity) || quantity < 1) return null
  const unit = quantityUnit ? canonicalPantryQuantityUnit(quantityUnit) || null : null
  return { name: cleaned, quantity, quantity_unit: unit }
}

/**
 * Parse an optional count (and container unit) from a single add line.
 * Supports: "2 lemons", "2x lemons", "Two lemons", "lemons (2)", "lemons - 2",
 * "Pineapple juice - 6 cans", "beans (2 cans)", "2 lbs of chicken thighs",
 * "chicken thighs 2 lbs".
 */
export function parsePantryAddQuantity(raw) {
  const s = String(raw || '').trim()
  if (!s) return { name: s, quantity: null, quantity_unit: null }

  let match = s.match(/^(\d+)\s*[xX×]\s+(.+)$/)
  if (match) {
    const rest = parsePantryAddQuantity(match[2])
    const got = pantryAddQuantityResult(rest.name, parseInt(match[1], 10), rest.quantity_unit)
    if (got) return got
  }

  match = s.match(WORD_COUNT_PATTERN)
  if (match) {
    const quantity = WORD_COUNTS[match[1].toLowerCase()]
    const rest = parsePantryAddQuantity(match[2])
    const got = pantryAddQuantityResult(rest.name, quantity, rest.quantity_unit)
    if (got) return got
  }

  // Leading "6 cans pineapple juice" / "6 cans of beans"
  match = s.match(new RegExp(`^(\\d{1,2})\\s+(${COUNT_UNIT_PATTERN})\\s+(?:of\\s+)?(.+)$`, 'i'))
  if (match) {
    const got = pantryAddQuantityResult(match[3], parseInt(match[1], 10), match[2])
    if (got) return got
  }

  // Leading "2 lbs chicken thighs" / "2 lbs of chicken thighs" / "2lb chicken thighs"
  match = s.match(new RegExp(`^(\\d{1,2})\\s*(${MEASURE_UNIT_PATTERN})\\.?\\s+(?:of\\s+)?(.+)$`, 'i'))
  if (match) {
    const got = pantryAddQuantityResult(match[3], parseInt(match[1], 10), match[2])
    if (got) return got
  }

  match = s.match(/^(\d{1,2})\s+(.+)$/)
  if (match) {
    const quantity = parseInt(match[1], 10)
    const name = (match[2] || '').trim()
    if (quantity >= 1 && quantity <= 99 && name && !LEADING_MEASURE_UNIT_PATTERN.test(name)) {
      const rest = parsePantryAddQuantity(name)
      const got = pantryAddQuantityResult(rest.name, quantity, rest.quantity_unit)
      if (got) return got
    }
  }

  // Receipt-style package size + trailing count: "Guacamole Mild - 14 OZ 1"
  match = s.match(
    new RegExp(
      `^(.+?)\\s*[-–—,]\\s*\\d+(?:\\.\\d+)?\\s+(${PACKAGE_SIZE_UNIT_ALT})\\b\\.?\\s+(\\d+)\\s*$`,
      'i',
    ),
  )
  if (match) {
    const got = pantryAddQuantityResult(match[1], parseInt(match[3], 10))
    if (got) return got
  }

  // Receipt-style package size only: "Guacamole Mild - 14 OZ" → clean name, no count.
  match = s.match(
    new RegExp(
      `^(.+?)\\s*[-–—,]\\s*\\d+(?:\\.\\d+)?\\s+(${PACKAGE_SIZE_UNIT_ALT})\\b\\.?\\s*$`,
      'i',
    ),
  )
  if (match) {
    const cleaned = (match[1] || '').trim()
    if (cleaned && /[A-Za-z]/.test(cleaned)) {
      return { name: cleaned, quantity: null, quantity_unit: null }
    }
  }

  // Trailing "Pineapple juice - 6 cans" / "beans – 2 bags"
  match = s.match(new RegExp(`^(.+?)\\s*[-–—]\\s*(\\d+)\\s+(${COUNT_UNIT_PATTERN})\\s*$`, 'i'))
  if (match) {
    const got = pantryAddQuantityResult(match[1], parseInt(match[2], 10), match[3])
    if (got) return got
  }

  // Trailing "beans 6 cans" (no dash)
  match = s.match(new RegExp(`^(.+?)\\s+(\\d+)\\s+(${COUNT_UNIT_PATTERN})\\s*$`, 'i'))
  if (match) {
    const got = pantryAddQuantityResult(match[1], parseInt(match[2], 10), match[3])
    if (got) return got
  }

  // Parenthetical "beans (2 cans)" / "beans (2)"
  match = s.match(new RegExp(`^(.+?)\\s*\\(\\s*(\\d+)\\s+(${COUNT_UNIT_PATTERN})\\s*\\)\\s*$`, 'i'))
  if (match) {
    const got = pantryAddQuantityResult(match[1], parseInt(match[2], 10), match[3])
    if (got) return got
  }

  match = s.match(/^(.+?)\s*\(\s*(\d+)\s*\)\s*$/)
  if (match) {
    const got = pantryAddQuantityResult(match[1], parseInt(match[2], 10))
    if (got) return got
  }

  match = s.match(/^(.+?)\s*[-–—]\s*(\d+)\s*$/)
  if (match) {
    const got = pantryAddQuantityResult(match[1], parseInt(match[2], 10))
    if (got) return got
  }

  // Trailing "chicken thighs 2 lbs" / "butter 8 oz"
  match = s.match(new RegExp(`^(.+?)\\s+(\\d{1,2})\\s*(${MEASURE_UNIT_PATTERN})\\.?\\s*$`, 'i'))
  if (match) {
    const got = pantryAddQuantityResult(match[1], parseInt(match[2], 10), match[3])
    if (got) return got
  }

  return { name: s, quantity: null, quantity_unit: null }
}

/**
 * Combine one typed line with the amount and unit fields.
 * The fields win over a number typed in the name. A chosen unit with no amount is 1.
 */
export function resolvePantryAddAmount(line, { amount = '', unit = '' } = {}) {
  const parsed = parsePantryAddQuantity(line)
  const fieldText = String(amount || '').trim()
  const fieldQty = fieldText ? parseInt(fieldText, 10) : null
  const chosenUnit = canonicalPantryQuantityUnit(unit) || null
  const parsedUnit = parsed.quantity_unit || null
  const quantity =
    fieldQty != null && Number.isFinite(fieldQty) && fieldQty > 0
      ? fieldQty
      : parsed.quantity != null && parsed.quantity > 0
        ? parsed.quantity
        : chosenUnit
          ? 1
          : null
  return {
    name: (parsed.name || String(line || '').trim()),
    quantity,
    quantity_unit: quantity != null ? chosenUnit || parsedUnit || null : null,
  }
}

/** One pantry line the batch importer can parse back into a name, amount, and unit. */
export function formatPantryAddLine({ name, quantity, quantity_unit }) {
  const cleaned = String(name || '').trim()
  if (!cleaned) return ''
  if (quantity != null && quantity > 0 && quantity_unit) {
    return `${quantity} ${quantity_unit} ${cleaned}`
  }
  if (quantity != null && quantity > 0) return `${quantity} ${cleaned}`
  return cleaned
}

/** Weight / volume units: running low when at most 25% of original remains. */
const MEASURE_QUANTITY_UNITS = new Set([
  'oz',
  'ounce',
  'ounces',
  'lb',
  'lbs',
  'pound',
  'pounds',
  'cup',
  'cups',
  'ml',
  'liter',
  'litre',
  'gallon',
  'gallons',
  'g',
  'kg',
])

/** Counts at or above this use fraction-of-original (same as measures), not only "1 left". */
const LARGE_COUNT_ORIGINAL_THRESHOLD = 6

/** At or below this share of original stock counts as running low for measures / large counts. */
const RUNNING_LOW_FRACTION = 0.25

/** Fresh/frozen shrimp is usually cooked in one–two batches, not drip-depleted. */
const BATCH_COOK_SEAFOOD_RE = /\b(shrimps?|prawns?)\b/i
/** Jarred shrimp condiments can still meaningfully "run low". */
const SHRIMP_CONDIMENT_RE = /\bshrimp\s+(paste|sauce)\b/i

function isMeasureQuantityUnit(item) {
  const unit = (item?.quantity_unit || '').trim().toLowerCase()
  return MEASURE_QUANTITY_UNITS.has(unit)
}

function skipsRunningLowAlert(item) {
  const name = (item?.item_name || item?.name || '').trim()
  if (!name) return false
  if (SHRIMP_CONDIMENT_RE.test(name)) return false
  return BATCH_COOK_SEAFOOD_RE.test(name)
}

/**
 * Running low when stock has been depleted from a known original amount:
 * - Count items (e.g. lemons): originally multiple, now exactly 1 left.
 * - Measure units (oz/lb/…) or larger counts (e.g. 12 eggs): at most 25% of original left
 *   (e.g. 10 lb chicken → 2 lb).
 * - Batch-cook seafood (shrimp/prawn) is excluded — leftovers are rarely kept as partial stock.
 */
export function isRunningLowCountable(item) {
  if (skipsRunningLowAlert(item)) return false

  const qty = Number(item?.quantity)
  const original = Number(item?.original_quantity)
  if (!Number.isFinite(qty) || !Number.isFinite(original)) return false
  if (qty < 1 || original < 1 || qty >= original) return false

  if (isMeasureQuantityUnit(item) || original >= LARGE_COUNT_ORIGINAL_THRESHOLD) {
    return qty / original <= RUNNING_LOW_FRACTION
  }

  return qty === 1 && original > 1
}

export function normalizePantryListInput(raw) {
  return String(raw || '')
    .replace(/\uFEFF/g, '')
    .replace(/[\u200B-\u200D\u2060]/g, '')
    .replace(/[\uFF0C\u060C\u3001\uFE50\uFE51]/g, ',')
    .replace(/[\u2028\u2029]/g, '\n')
}

export async function parseJsonResponse(res) {
  const text = await res.text()
  const trimmed = (text || '').trim()
  if (trimmed.startsWith('<') || trimmed.toLowerCase().includes('<!doctype')) {
    throw new Error(
      'Server returned a page instead of data. In production, ensure the backend is running and /inventory is proxied to it.',
    )
  }
  try {
    return trimmed ? JSON.parse(text) : {}
  } catch {
    throw new Error('Invalid response from server. Please try again.')
  }
}

export function idsFromBuckets(buckets) {
  if (!buckets || typeof buckets !== 'object') return []
  const ids = []
  for (const key of ['proteins', 'fruits_vegetables', 'dairy', 'spices', 'staples']) {
    const arr = buckets[key]
    if (Array.isArray(arr)) {
      for (const it of arr) {
        if (it?.id != null) ids.push(String(it.id))
      }
    }
  }
  return ids
}

/** Unique accordion bucket keys for inventory items (desktop auto-expand after add). */
export function bucketKeysFromItems(items) {
  const keys = new Set()
  for (const item of items || []) {
    keys.add(categoryToBucketKey(item?.category))
  }
  return [...keys]
}

/** Accordion bucket keys that have at least one item in a receipt/buckets payload. */
export function nonEmptyBucketKeys(buckets) {
  if (!buckets || typeof buckets !== 'object') return []
  return PANTRY_CATEGORIES.map(({ key }) => key).filter(
    (key) => Array.isArray(buckets[key]) && buckets[key].length > 0,
  )
}

export function notifyPantryInventoryChanged() {
  try {
    window.dispatchEvent(new CustomEvent('pantry-inventory-changed'))
  } catch {
    /* ignore */
  }
}

/**
 * Contextual eyebrow only when it adds information the item-count eyebrow does not.
 * Conflicts removed (do not restore without a distinct surface):
 * - "On hand" — the count is the eyebrow once the pantry has items
 * - "Use it soon" — duplicates the Expiring chip / "to use in the next few days" line
 */
export function getPantryContextEyebrow({
  loading,
  totalItems,
  showPantryStarter,
  lowStock,
  isPantryShrinkingState,
}) {
  if (loading) return null
  if (totalItems === 0) {
    return showPantryStarter ? 'Nothing added yet' : 'Quick-add staples'
  }
  if (lowStock && isPantryShrinkingState) return 'Running low'
  return null
}

/** "12 items on hand" / "1 item on hand" for the Pantry eyebrow. */
export function formatPantryOnHandLabel(totalItems) {
  const count = Number(totalItems) || 0
  return `${count} ${count === 1 ? 'item' : 'items'} on hand`
}

/**
 * Eyebrow above the Pantry h1.
 * The item count owns the slot once anything is on hand. Empty-state copy
 * covers a pantry with nothing in it. Last updated is not shown.
 */
export function getPantryPageEyebrow({
  totalItems = 0,
  contextEyebrow,
  loading,
  fallbackEyebrow = 'Pantry',
}) {
  if (loading) return 'Loading pantry…'
  if (totalItems > 0) return formatPantryOnHandLabel(totalItems)
  if (contextEyebrow) return contextEyebrow
  return fallbackEyebrow
}

/** Update session peak count; resets to 0 when pantry is empty. */
export function computePeakPantryCount(previousPeak, totalItems) {
  if (totalItems === 0) return 0
  return Math.max(previousPeak, totalItems)
}

/** Low-stock nudges apply when count drops below a prior peak, not while stocking up. */
export function isPantryShrinking(totalItems, peakCount) {
  return totalItems > 0 && totalItems < peakCount
}

/** Hide redundant expiring bands right after adds (stat chips already show the count). */
export function shouldSuppressExpiringPromo({ suppressUntilMs, nowMs, recentlyAddedIds }) {
  if ((recentlyAddedIds?.length ?? 0) > 0) return true
  if (suppressUntilMs > nowMs) return true
  return false
}

/** Split manual pantry add text into individual item segments. */
export function getPantryAddSegments(raw) {
  const normalized = normalizePantryListInput(raw).trim()
  if (!normalized) return []
  return normalized
    .split(/[\n,;|•]+/g)
    .map((s) => s.trim())
    .filter(Boolean)
}

/**
 * Replace one segment in pantry add input with a spelling suggestion.
 * When the segment is the only one, returns the suggestion alone.
 * Preserves a trailing weight/size (e.g. "0.5 lb") from the original segment.
 */
export function applyPantrySpellingToInput(fullText, segmentInput, suggestion) {
  const normalized = normalizePantryListInput(fullText)
  const segments = getPantryAddSegments(normalized)
  const suggestionWithMeasure = preserveTrailingWeightMeasure(segmentInput, suggestion)
  if (segments.length === 0) return suggestionWithMeasure
  if (segments.length === 1) return suggestionWithMeasure

  const target = (segmentInput || '').trim()
  let replaced = false
  const next = segments.map((segment) => {
    if (!replaced && segment === target) {
      replaced = true
      return suggestionWithMeasure
    }
    return segment
  })
  if (!replaced) return fullText
  return next.join(', ')
}

const TRAILING_WEIGHT_MEASURE_RE = /\s+(\d+(?:\.\d+)?)\s*(oz|ounces?|lbs?|pounds?|kg|g)\b\.?\s*$/i

/** Keep "0.5 lb" (etc.) when a spelling suggestion replaces only the product name. */
export function preserveTrailingWeightMeasure(original, suggestion) {
  const src = String(original || '')
  const next = String(suggestion || '').trim()
  if (!next) return next
  const match = src.match(TRAILING_WEIGHT_MEASURE_RE)
  if (!match) return next
  const measure = `${match[1]} ${match[2]}`
  if (new RegExp(`\\b${measure.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(next)) {
    return next
  }
  return `${next} ${measure}`.trim()
}
